import { counterPick } from "../court/armyScript";
import { type AxialCoord, hexDistance, hexEquals, hexKey, hexNeighbors } from "../hex";
import { buildStructure, isBuildable, nextImprovement } from "../land/build";
import { ownWorkingCity, workingCities } from "../land/cities";
import { nearestTo, rankBy } from "../pick";
import { findHex, mapOrder, unitAt, unitById, unitsOf } from "../run/board";
import { isProductionTick, onCadence } from "../run/cadence";
import { supplyOf } from "../run/pools";
import { isHeldUnit, isSeatUnit, scriptUnitsOf } from "../run/seats";
import { allied, hostile, sameOwner, sideOf } from "../run/sides";
import type { CityCommandRules } from "../types/rules";
import type { FactionId, HexState, RunState, Unit, UnitKind } from "../types/state";
import { isBroken } from "../units/morale";
import { buildUnit } from "../units/roster";
import { cancelUnitOrder, orderUnit, road } from "../units/routes";
import { updateUnits } from "../units/unit";
import {
  cityLandOf,
  commandedCity,
  homedAt,
  isCityCommand,
  isColumnFull,
  isHandHeld,
  isTaking,
} from "./cityVerbs";

/**
 * City Command's steps: every yard trains by itself (cityYards) and every city sends its column where
 * its arrow points, mustering before walls, holding, or guarding Home (cityDuty).
 */

/** The kind a city trains next (CC1): its own, the counter-pick on Auto, none on Pause. */
export function cityKind(
  state: RunState,
  faction: FactionId,
  city: HexState,
): UnitKind | undefined {
  return city.makes === "pause" ? undefined : (city.makes ?? counterPick(state, faction));
}

/**
 * A city's yard trains (CC1): idle, not paused and short of `unitsPerCity` soldiers of its own,
 * it starts its kind through the recruit rule as a delegate (no Will, no reach), so the room,
 * the price under the levy and the recruit still bind. Same object back when it cannot.
 */
export function yardTrains(state: RunState, faction: FactionId, coord: AxialCoord): RunState {
  const city = findHex(state, coord);
  if (city?.unitBuild !== "none" || isColumnFull(state, coord)) {
    return state;
  }
  // The kind last: the counter-pick reads the whole map, and a busy or full yard needs none (war-speed).
  const kind = cityKind(state, faction, city);
  return kind ? buildUnit(state, faction, coord, kind, "mayor") : state;
}

/**
 * A full city with an idle yard develops (CC1): a farm or a hamlet in turn over its land (the
 * rule of A54's mayor), on the free hex of it nearest the city, while the pool keeps
 * `developFloor` after the price. Only with the improvements block.
 */
function developCity(state: RunState, faction: FactionId, city: HexState): RunState {
  const improvements = state.rules.improvements;
  const floor = state.rules.cityCommand?.developFloor ?? 0;
  if (!improvements || supplyOf(state, faction) - improvements.cost < floor) {
    return state;
  }
  const land = cityLandOf(state, faction, city.coord);
  const kind = nextImprovement(state, land);
  const [site] = rankBy(
    land.filter((hex) => isBuildable(state, faction, hex.coord, kind, "mayor")),
    (hex) => hexDistance(hex.coord, city.coord),
  );
  return site ? buildStructure(state, faction, site.coord, kind, "mayor") : state;
}

/** One city's yard on the production step (CC1): it trains when it can; full of its own, idle and not paused, it develops. */
function yardTurn(state: RunState, faction: FactionId, coord: AxialCoord): RunState {
  const trained = yardTrains(state, faction, coord);
  const city = findHex(state, coord);
  const full =
    city !== undefined &&
    city.unitBuild === "none" &&
    city.makes !== "pause" &&
    isColumnFull(state, coord);
  return trained === state && city && full ? developCity(state, faction, city) : trained;
}

/** Step 1c', on the production interval (CC1): every working city of a city-command kingdom takes its yard's turn, in map order. */
export function cityYards(state: RunState): RunState {
  if (!state.rules.cityCommand || !isProductionTick(state)) {
    return state;
  }
  let next = state;
  for (const faction of state.factions) {
    if (!isCityCommand(state, faction)) {
      continue;
    }
    for (const { coord } of workingCities(state, faction)) {
      next = yardTurn(next, faction, coord);
    }
  }
  return next;
}

/**
 * One place a city's duty may send a soldier (CC1). Lower tiers are filled first; a shared goal
 * takes every soldier sent to it (a walled target, ground down together); a group caps how many
 * of its goals are taken (the guard beside one intruder).
 */
interface DutyGoal {
  readonly coord: AxialCoord;
  readonly tier: number;
  /** The hex's place on the map: the last tie-break. */
  readonly order: number;
  readonly shared?: true;
  readonly group?: number;
  readonly quota?: number;
}

/** The last own hex on the city's road to `target` (CC1): where a column gathers before walls. */
function musterHex(
  state: RunState,
  faction: FactionId,
  city: AxialCoord,
  target: AxialCoord,
): AxialCoord | undefined {
  const route = road(state.hexes, sideOf(state.rules, faction), city, target);
  if (!route) {
    return undefined;
  }
  let muster = city;
  for (const coord of route.slice(1)) {
    if (!sameOwner(findHex(state, coord)?.owner, faction)) {
      break;
    }
    muster = coord;
  }
  return muster;
}

/**
 * The column has gathered before the walls (CC1): `musterSize` of its free soldiers stand at or
 * beside the muster hex or are already at the target or walking to it, or `musterTicks` have
 * passed since the arrow was set.
 */
function mustered(
  state: RunState,
  rules: CityCommandRules,
  city: HexState,
  muster: AxialCoord,
  target: AxialCoord,
  free: readonly Unit[],
): boolean {
  if (state.tick - (city.arrowTick ?? state.tick) >= rules.musterTicks) {
    return true;
  }
  const gathered = free.filter(
    (unit) =>
      hexDistance(unit.coord, muster) <= 1 ||
      hexDistance(unit.coord, target) <= rules.rallyRadius ||
      (unit.destination !== undefined && hexEquals(unit.destination, target)),
  ).length;
  return gathered >= rules.musterSize;
}

/**
 * Where a city's free soldiers go (CC1). An arrow on foreign or neutral land: before walls the
 * muster hex and its ring, then the target for all of them; a wall-free target first, then the
 * foreign wall-free land of its ring. An arrow on own land (one set there, or a target taken):
 * the target, then the neutral wall-free land of its ring, which is taken, then the own land of
 * its ring, to hold; never a rival's. Under an arrow a soldier left without a place waits on the
 * own land nearest the target. Home: beside each rival unit in the city's reach (a quota
 * each), then the nearest neutral wall-free land in its reach, then beside the keep; never rival
 * land beyond the reach. `claimed` is where the kingdom's other soldiers stand or walk.
 */
function dutyGoals(
  state: RunState,
  rules: CityCommandRules,
  faction: FactionId,
  city: HexState,
  free: readonly Unit[],
  claimed: ReadonlySet<string>,
): DutyGoal[] {
  const position = mapOrder(state.hexes);
  const goal = (hex: HexState, tier: number): DutyGoal => ({
    coord: hex.coord,
    tier,
    order: position(hex.coord),
  });
  const open = (hex: HexState): boolean => !hex.razed && !claimed.has(hexKey(hex.coord));
  const target = city.arrow === undefined ? undefined : findHex(state, city.arrow);
  if (!target || target.razed) {
    return homeGoals(state, rules, faction, city, goal, open);
  }
  const goals = arrowGoals(state, rules, faction, city, target, free, goal, open);
  return [...goals, ...waitingGoals(state, faction, target, goals, goal, open)];
}

/**
 * Where a city's soldiers go under its arrow (CC1): the work of `dutyGoals` for a target on the
 * map, waiting aside.
 */
function arrowGoals(
  state: RunState,
  rules: CityCommandRules,
  faction: FactionId,
  city: HexState,
  target: HexState,
  free: readonly Unit[],
  goal: (hex: HexState, tier: number) => DutyGoal,
  open: (hex: HexState) => boolean,
): DutyGoal[] {
  const ring = (hex: HexState): boolean =>
    hexDistance(hex.coord, target.coord) <= rules.rallyRadius;
  const tierOf = (hex: HexState): number => (hexEquals(hex.coord, target.coord) ? 0 : 1);
  if (allied(state.rules, target.owner, faction)) {
    const holding = (hex: HexState): number | undefined =>
      hexEquals(hex.coord, target.coord)
        ? 0
        : hex.owner === "none" && hex.walls === 0
          ? 1
          : allied(state.rules, hex.owner, faction)
            ? 2
            : undefined;
    return state.hexes.flatMap((hex) => {
      const tier = ring(hex) && open(hex) ? holding(hex) : undefined;
      return tier === undefined ? [] : [goal(hex, tier)];
    });
  }
  if (target.walls > 0) {
    const muster = musterHex(state, faction, city.coord, target.coord);
    if (muster && !mustered(state, rules, city, muster, target.coord, free)) {
      return state.hexes
        .filter((hex) => hex.owner === faction && hexDistance(hex.coord, muster) <= 1 && open(hex))
        .map((hex) => goal(hex, hexEquals(hex.coord, muster) ? 0 : 1));
    }
    return [{ ...goal(target, 0), shared: true }];
  }
  return state.hexes
    .filter(
      (hex) =>
        ring(hex) && !allied(state.rules, hex.owner, faction) && hex.walls === 0 && open(hex),
    )
    .map((hex) => goal(hex, tierOf(hex)));
}

/**
 * Where the soldiers an arrow has no place for wait (CC1): the own land nearest its target, one
 * tier a hex of distance after every working goal, so they close in as places open; never on a
 * hex a rival stands on, which would be an attack, not a wait.
 */
function waitingGoals(
  state: RunState,
  faction: FactionId,
  target: HexState,
  goals: readonly DutyGoal[],
  goal: (hex: HexState, tier: number) => DutyGoal,
  open: (hex: HexState) => boolean,
): DutyGoal[] {
  const listed = new Set(goals.map((candidate) => hexKey(candidate.coord)));
  const after = goals.reduce((highest, candidate) => Math.max(highest, candidate.tier), -1) + 1;
  return state.hexes
    .filter(
      (hex) =>
        hex.owner === faction &&
        open(hex) &&
        !listed.has(hexKey(hex.coord)) &&
        noRivalOn(state, faction, hex),
    )
    .map((hex) => goal(hex, after + hexDistance(hex.coord, target.coord)));
}

function homeGoals(
  state: RunState,
  rules: CityCommandRules,
  faction: FactionId,
  city: HexState,
  goal: (hex: HexState, tier: number) => DutyGoal,
  open: (hex: HexState) => boolean,
): DutyGoal[] {
  const reach = state.rules.cityReach;
  const near = (coord: AxialCoord): boolean => hexDistance(coord, city.coord) <= reach;
  // Home stands on land, never in a rival's boots: an intruder is the guard's tier.
  const free = (hex: HexState): boolean => open(hex) && noRivalOn(state, faction, hex);
  const intruders = rankBy(
    state.units.filter((unit) => hostile(state.rules, unit.owner, faction) && near(unit.coord)),
    (unit) => hexDistance(unit.coord, city.coord),
    (unit) => unit.id,
  );
  const goals: DutyGoal[] = [];
  intruders.forEach((intruder, group) => {
    for (const coord of hexNeighbors(intruder.coord)) {
      const hex = findHex(state, coord);
      const enterable =
        hex !== undefined &&
        free(hex) &&
        (hex.owner === faction || hex.walls === 0) &&
        (hex.owner === faction || hex.owner === "none" || near(coord));
      if (hex && enterable) {
        goals.push({ ...goal(hex, 0), group, quota: rules.homeGuardPerIntruder });
      }
    }
  });
  for (const hex of state.hexes) {
    if (hex.owner === "none" && hex.walls === 0 && near(hex.coord) && free(hex)) {
      goals.push(goal(hex, 1));
    }
  }
  for (const hex of state.hexes) {
    if (hex.owner === faction && hexDistance(hex.coord, city.coord) === 1 && free(hex)) {
      goals.push(goal(hex, 2));
    }
  }
  return goals;
}

/** No rival unit stands on the hex: a soldier waiting or guarding there would be attacking. */
function noRivalOn(state: RunState, faction: FactionId, hex: HexState): boolean {
  return (unitAt(state, hex.coord)?.owner ?? faction) === faction;
}

/**
 * A soldier sent on its duty to `to` (CC1): stopped when it stands there; undefined when no road
 * leads there. A delegate's order ("mayor"), but a soldier seat's unit takes it as its own word
 * (K1): the duty is its script's hands, so its deeds are at work (A62) as a human's would be.
 */
function sendOnDuty(
  state: RunState,
  faction: FactionId,
  unitId: number,
  to: AxialCoord,
): RunState | undefined {
  const unit = unitById(state, unitId);
  if (!unit) {
    return undefined;
  }
  if (hexEquals(unit.coord, to)) {
    return unit.destination ? cancelUnitOrder(state, faction, unitId, "mayor") : state;
  }
  if (unit.destination && hexEquals(unit.destination, to)) {
    return state;
  }
  const ordered = orderUnit(
    state,
    faction,
    unitId,
    to,
    isSeatUnit(state, unitId) ? "soldier" : "mayor",
  );
  return ordered === state ? undefined : ordered;
}

/**
 * The stable deal (CC1), tier by tier: a soldier standing on a goal of the tier keeps it, then
 * one walking to one, then each soldier left (by id) takes the nearest goal of the tier still
 * open (distance, then map order). One soldier a goal but a shared one; a soldier with no goal
 * left waits where it stands. Its orders go out as a delegate's ("mayor"), never the King's (a
 * seat's unit's as its own, `sendOnDuty`).
 */
function assignDuty(
  state: RunState,
  faction: FactionId,
  free: readonly Unit[],
  goals: readonly DutyGoal[],
): RunState {
  const taken = new Set<string>();
  const filled = new Map<number, number>();
  const fits = (goal: DutyGoal): boolean =>
    (goal.shared || !taken.has(hexKey(goal.coord))) &&
    (goal.group === undefined || (filled.get(goal.group) ?? 0) < (goal.quota ?? 0));
  const claim = (goal: DutyGoal): void => {
    if (!goal.shared) {
      taken.add(hexKey(goal.coord));
    }
    if (goal.group !== undefined) {
      filled.set(goal.group, (filled.get(goal.group) ?? 0) + 1);
    }
  };
  const placed = new Set<number>();
  let next = state;
  const tiers = [...new Set(goals.map((goal) => goal.tier))].sort((a, b) => a - b);
  const keep = (inTier: readonly DutyGoal[], at: (unit: Unit) => AxialCoord | undefined): void => {
    for (const unit of free) {
      const coord = at(unit);
      const kept =
        placed.has(unit.id) || coord === undefined
          ? undefined
          : inTier.find((goal) => hexEquals(goal.coord, coord) && fits(goal));
      if (kept) {
        claim(kept);
        placed.add(unit.id);
      }
    }
  };
  for (const tier of tiers) {
    const inTier = goals.filter((goal) => goal.tier === tier);
    keep(inTier, (unit) => (unit.destination ? undefined : unit.coord));
    keep(inTier, (unit) => unit.destination);
    for (const unit of free) {
      if (placed.has(unit.id)) {
        continue;
      }
      const ranked = rankBy(
        inTier.filter(fits),
        (goal) => hexDistance(goal.coord, unit.coord),
        (goal) => goal.order,
      );
      for (const goal of ranked) {
        const sent = sendOnDuty(next, faction, unit.id, goal.coord);
        if (sent) {
          next = sent;
          claim(goal);
          placed.add(unit.id);
          break;
        }
      }
    }
  }
  for (const unit of free) {
    if (!placed.has(unit.id) && unit.destination) {
      next = cancelUnitOrder(next, faction, unit.id, "mayor");
    }
  }
  return next;
}

/**
 * One city's duty (CC1): its free soldiers (its column less the human's, the broken, the ones
 * the King holds by hand and the ones taking the land they stand on; with the unheld soldier seats
 * homed on it, K1, less one hauling a load to market) are dealt the goals of its arrow, or of Home.
 */
export function cityTurn(state: RunState, faction: FactionId, coord: AxialCoord): RunState {
  const rules = state.rules.cityCommand;
  const city = commandedCity(state, faction, coord);
  if (!rules || !city) {
    return state;
  }
  const free = scriptUnitsOf(state, faction).filter(
    (unit) =>
      homedAt(unit, coord) &&
      unit.carry === undefined &&
      !isBroken(state, unit) &&
      !isHandHeld(state, unit) &&
      !isTaking(state, unit),
  );
  if (free.length === 0) {
    return state;
  }
  const ids = new Set(free.map((unit) => unit.id));
  const claimed = new Set<string>();
  for (const unit of unitsOf(state, faction)) {
    if (ids.has(unit.id)) {
      continue;
    }
    claimed.add(hexKey(unit.coord));
    if (unit.destination) {
      claimed.add(hexKey(unit.destination));
    }
  }
  return assignDuty(state, faction, free, dutyGoals(state, rules, faction, city, free, claimed));
}

/** A soldier whose city is no working city of its kingdom joins the nearest one (CC1; map order on ties). */
function rehome(state: RunState, faction: FactionId): RunState {
  const cities = workingCities(state, faction);
  if (cities.length === 0) {
    return state;
  }
  return updateUnits(state, (unit) => {
    const home = unit.home;
    if (
      !sameOwner(unit.owner, faction) ||
      isHeldUnit(state, unit.id) ||
      (home !== undefined && ownWorkingCity(state, faction, home) !== undefined)
    ) {
      return unit;
    }
    const nearest = nearestTo(cities, unit.coord);
    return nearest ? { ...unit, home: nearest.coord } : unit;
  });
}

/**
 * End of tick (CC1), every `dutyCadenceTicks`, on the tick before a move step so the orders march
 * at once: each city-command kingdom, in resolution order, re-homes the soldiers whose city fell,
 * then every working city of it, in map order, gives its free soldiers their duty.
 */
export function cityDuty(state: RunState): RunState {
  const rules = state.rules.cityCommand;
  if (!rules || state.status !== "running" || !onCadence(state, rules.dutyCadenceTicks, 1)) {
    return state;
  }
  let next = state;
  for (const faction of state.factions) {
    if (!isCityCommand(next, faction)) {
      continue;
    }
    next = rehome(next, faction);
    for (const { coord } of workingCities(next, faction)) {
      next = cityTurn(next, faction, coord);
    }
  }
  return next;
}
